// Step 3: Requirements Component - Mantine Version
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Text, Box, Group, Badge } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { RequirementInput } from '../../../../ui/RequirementInput';
import { RequirementsList } from '../../../../ui/RequirementsList';
import { useRequirements } from '../../../../../hooks/useRequirements';
import { useRequirementSuggestions } from '../../../../../hooks/useRequirementSuggestions';
import { MantineWizardFormData } from '../../utils/mantineFormSchema';
import { FormSection } from '../../../../common/form';

import classes from './Step3Requirements.module.css';
import sharedClasses from '../../../../common/styles/ScrollContainerLayout.module.css';

interface Step3RequirementsProps {
  form: UseFormReturnType<MantineWizardFormData>;
  isLoading?: boolean;
}

export const Step3Requirements = ({
  form,
  isLoading = false,
}: Step3RequirementsProps) => {
  const { t } = useTranslation();
  const [newRequirementText, setNewRequirementText] = useState('');

  // Extract suggestion data and requirements logic into custom hooks
  const { autocompleteData, categoryColors } = useRequirementSuggestions();
  const {
    displayRequirements,
    totalCount,
    addRequirement,
    removeRequirement,
    updateRequirement,
    handleDragEnd,
    handleKeyPress,
    scrollContainerRef,
  } = useRequirements({ form, isLoading });

  // Handle adding new requirement
  const handleAddRequirement = () => {
    if (newRequirementText.trim()) {
      addRequirement(newRequirementText);
      setNewRequirementText('');
    }
  };

  // Handle key press for input
  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    handleKeyPress(event, newRequirementText, handleAddRequirement);
  };

  return (
    <FormSection
      title={t('wizard.step3.heading')}
      description={t('wizard.step3.description')}
    >
      <Box className={classes.container}>
        {/* Header */}
        <Stack
          gap="lg"
          className={`${sharedClasses.header} ${totalCount > 0 ? classes.headerWithBorder : ''}`}
        >
          {/* Add New Requirement */}
          <RequirementInput
            value={newRequirementText}
            onChange={setNewRequirementText}
            onAdd={handleAddRequirement}
            onKeyPress={onKeyPress}
            disabled={isLoading}
            autocompleteData={autocompleteData}
            categoryColors={categoryColors}
          />

          {/* Requirements Counter - Always visible when there are items */}
          {totalCount > 0 && (
            <Group justify="space-between" align="center">
              <Text size="sm" fw={500}>
                {t('wizard.step3.requirementsCount')}
              </Text>
              <Badge variant="light" size="lg">
                {totalCount} {t('common.items', { defaultValue: 'items' })}
              </Badge>
            </Group>
          )}
        </Stack>

        {/* Scrollable Content — only the list scrolls; bar floats in the gutter */}
        <AppScrollArea
          fill
          fullBleedGutter="md"
          viewportRef={scrollContainerRef}
        >
          <RequirementsList
            requirements={displayRequirements}
            onUpdateRequirement={updateRequirement}
            onRemoveRequirement={removeRequirement}
            onDragEnd={handleDragEnd}
            isLoading={isLoading}
            formError={form.errors.requirements}
          />
        </AppScrollArea>
      </Box>
    </FormSection>
  );
};
